import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OverlayPanel } from 'primeng/overlaypanel';

import { ReservationService } from '@app/_components/reservation';
import { PropertyService } from '@app/_components/property';
import { OwnerProfileService } from '@app/_components/owner-profile/owner-profile.service';
import { ReservationSelectComponent } from '@app/_components/reservation/components/reservation-select/reservation-select.component';
import { InboxMailSMsComponent } from '../inbox-mail-sms/inbox-mail-sms.component';
import { AlertService } from '@app/_services';

/** Context IDs as defined in InboxService */
const CTX_RESERVATION = 7;
const CTX_HOMEOWNER   = 8;

export type DirectoryMode = 'reservation' | 'homeowner';
export type OwnerSearchMode = 'name' | 'property';

@Component({
	selector:    'app-email-directory',
	templateUrl: './email-directory.component.html',
	styleUrls:   ['./email-directory.component.css'],
	standalone:  false,
})
export class EmailDirectoryComponent implements OnInit, OnDestroy
{
	
	@Input() msgContextIds: number[] = [];

	@ViewChild('directoryPanel') directoryPanel!: OverlayPanel;
	@ViewChild('reservationSelector') reservationSelector?: ReservationSelectComponent;

	private readonly destroy$ = new Subject<void>();

	activeMode: DirectoryMode = 'reservation';

	selectedReservation: any = null;

	ownerSearchMode: OwnerSearchMode = 'name';

	readonly searchModeOptions = [
		{ label: 'By Owner Name',    value: 'name'     },
		{ label: 'By Property Code', value: 'property' },
	];

	ownerNameQuery = '';
	ownerResults:   any[] = [];
	allOwnerProfiles: any[] = [];

	selectedProperty:    any    = null;
	propertySuggestions: any[]  = [];
	allProperties:       any[]  = [];
	propertyOwners:      any[]  = [];

	selectedOwners: any[] = [];

	isLoading = false;


	get showButton(): boolean
	{
		return !!(
			this.msgContextIds?.includes(CTX_RESERVATION) ||
			this.msgContextIds?.includes(CTX_HOMEOWNER)
		);
	}

	get showBothModes(): boolean
	{
		return !!(
			this.msgContextIds?.includes(CTX_RESERVATION) &&
			this.msgContextIds?.includes(CTX_HOMEOWNER)
		);
	}

	get isReservationMode(): boolean
	{
		return this.showBothModes
			? this.activeMode === 'reservation'
			: !!this.msgContextIds?.includes(CTX_RESERVATION);
	}

	get isHomeownerMode(): boolean
	{
		return this.showBothModes
			? this.activeMode === 'homeowner'
			: !!this.msgContextIds?.includes(CTX_HOMEOWNER);
	}

	get canComposeForOwners(): boolean
	{
		return this.selectedOwners.length > 0;
	}

	get canComposeForReservation(): boolean
	{
		return !!(this.selectedReservation?.emailAddress);
	}

	constructor(
		private readonly dialog:              MatDialog,
		private readonly reservationService:  ReservationService,
		private readonly propertyService:     PropertyService,
		private readonly ownerProfileService: OwnerProfileService,
		private readonly alertService:        AlertService,
	) {}

	ngOnInit(): void
	{
		if (this.msgContextIds?.includes(CTX_HOMEOWNER) &&
			!this.msgContextIds?.includes(CTX_RESERVATION))
		{
			this.activeMode = 'homeowner';
		}

		this.loadOwnerDropDown();

		this.propertyService.propertySummaryForShortData().pipe(
			takeUntil(this.destroy$),
		).subscribe({
			next:  res => this.allProperties = res?.data?.properties ?? [],
			error: err => this.alertService.error(err),
		});
	}

	ngOnDestroy(): void
	{
		this.destroy$.next();
		this.destroy$.complete();
	}


	togglePanel(event: MouseEvent): void
	{
		this.directoryPanel.toggle(event);
	}

	resetPanel(): void
	{
		this.selectedReservation  = null;
		this.selectedOwners       = [];
		this.ownerResults         = [];
		this.propertyOwners       = [];
		this.selectedProperty     = null;
		this.ownerNameQuery       = '';
		this.isLoading            = false;
		this.clearReservationSearchField();
	}


	switchMode(mode: DirectoryMode): void
	{
		this.activeMode = mode;
		this.resetPanel();
	}

	onSearchModeChange(): void
	{
		this.selectedOwners   = [];
		this.ownerResults     = [];
		this.propertyOwners   = [];
		this.selectedProperty = null;
		this.ownerNameQuery   = '';
		this.isLoading        = false;
		if (this.ownerSearchMode === 'name') {
			this.ownerResults = [...this.allOwnerProfiles];
		}
	}


	onReservationSelected(reservation: any): void
	{
		this.selectedReservation = reservation;
	}

	composeForReservation(): void
	{
		if (!this.canComposeForReservation) { return; }
		this.directoryPanel.hide();
		this.clearReservationSearchField();
		this.openCompose({
			title:        'New Mail',
			reservations: [this.selectedReservation],
			msgContextId: CTX_RESERVATION,
		});
	}


	onOwnerNameInput(query: string): void
	{
		const normalizedQuery = (query ?? '').trim().toLowerCase();
		if (!normalizedQuery)
		{
			this.ownerResults = [...this.allOwnerProfiles];
			return;
		}

		this.ownerResults = this.allOwnerProfiles.filter((owner: any) => {
			const firstName = (owner?.firstName ?? '').toLowerCase();
			const lastName = (owner?.lastName ?? '').toLowerCase();
			const fullName = (owner?.fullName ?? '').toLowerCase();
			const emailAddress = (owner?.emailAddress ?? '').toLowerCase();
			const mobilePhone = (owner?.mobilePhone ?? '').toLowerCase();
			return firstName.includes(normalizedQuery)
				|| lastName.includes(normalizedQuery)
				|| fullName.includes(normalizedQuery)
				|| emailAddress.includes(normalizedQuery)
				|| mobilePhone.includes(normalizedQuery);
		});
	}

	onOwnerNameSearch(event: any): void
	{
		const query = event?.query ?? '';
		this.ownerNameQuery = query;
		this.onOwnerNameInput(query);
	}


	onPropertySearch(event: any): void
	{
		const q = (event.query ?? '').toLowerCase();
		this.propertySuggestions = this.allProperties.filter(p =>
			p.propertyCode?.toLowerCase().includes(q) ||
			p.shortTermRentalName?.toLowerCase().includes(q),
		);
	}

	onPropertySelected(event: any): void
	{
		const property = event?.value ?? event;

		this.selectedProperty = property;
		this.propertyOwners   = [];
		this.selectedOwners   = [];

		if (!property?.shortTermRentalId) { return; }

		this.isLoading = true;
		this.propertyService
			.ownerListByProperty(property.shortTermRentalId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res =>
				{
					this.propertyOwners = (res?.data?.ownerProfiles ?? [])
						.filter((o: any) => !!o.emailAddress);
					this.isLoading = false;
				},
				error: err =>
				{
					this.alertService.error(err);
					this.isLoading = false;
				},
			});
	}


	composeForOwners(): void
	{
		if (!this.canComposeForOwners) { return; }
		this.directoryPanel.hide();
		this.openCompose({
			title:        'New Mail',
			ownerEmails:  this.selectedOwners.map(o => ({
				email:     o.emailAddress,
				firstName: o.firstName,
				lastName:  o.lastName,
			})),
			msgContextId: CTX_HOMEOWNER,
		});
	}


	private openCompose(data: any): void
	{
		const cfg = new MatDialogConfig();
		cfg.disableClose = false;
		cfg.autoFocus    = true;
		cfg.hasBackdrop  = true;
		cfg.width        = '90vw';
		cfg.height       = '90vh';
		cfg.maxWidth     = '1400px';
		cfg.maxHeight    = '90vh';
		cfg.data         = data;
		this.dialog.open(InboxMailSMsComponent, cfg);
	}

	private loadOwnerDropDown(): void
	{
		this.isLoading = true;
		this.ownerProfileService
			.findOwnerProfileDropDown()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res =>
				{
					this.allOwnerProfiles = (res?.data?.ownerProfiles ?? [])
						.filter((owner: any) => !!owner.emailAddress);
					this.ownerResults = [...this.allOwnerProfiles];
					this.isLoading = false;
				},
				error: err =>
				{
					this.alertService.error(err);
					this.isLoading = false;
				},
			});
	}

	private clearReservationSearchField(): void
	{
		if (!this.reservationSelector) { return; }
		this.reservationSelector.reservationSelectForm?.reset();
		this.reservationSelector.reservation = null;
		this.reservationSelector.reservationSuggestions = [];
	}
}
